/**
 * Saved alerts: what each one has caught since it was last read.
 *
 * Two decisions here are product decisions rather than layout:
 *
 * 1. **Opening an alert does not mark it read.** A glance would otherwise
 *    destroy the list before it had been looked at, and there is no way back —
 *    the mark only moves forward. "Mark as read" is a button someone presses.
 * 2. **An alert the plan no longer covers is shown, not hidden.** The server
 *    keeps it (a lapsed card is usually temporary) and reports `entitled:
 *    false`, so this page has three states to render rather than two: has
 *    news, has none, and cannot answer.
 */

import { useCallback, useEffect, useState } from "react";

import { ApiError, api } from "../api/client";
import type { WatchMatches, WatchSummary } from "../api/types";
import { useAuth } from "../auth/AuthProvider";
import {
  emptyReason,
  formatWhen,
  measuredFrom,
  newCountLabel,
} from "../lib/alerts";
import { countyName } from "../lib/counties";
import { DASHBOARD_PATH } from "../lib/route";
import { Link } from "../router";
import { BuildingsTable } from "./BuildingsTable";
import { UnitsTable } from "./UnitsTable";

export function AlertsPage() {
  const { user } = useAuth();
  const [summaries, setSummaries] = useState<WatchSummary[] | null>(null);
  const [open, setOpen] = useState<number | null>(null);
  const [matches, setMatches] = useState<WatchMatches | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (signal?: AbortSignal) => {
    try {
      const list = await api.watches(signal);
      setSummaries(list.watches);
      setError("");
    } catch (cause) {
      if (cause instanceof DOMException && cause.name === "AbortError") return;
      setSummaries([]);
      setError(cause instanceof ApiError ? cause.message : "Alerts are unavailable.");
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  async function openWatch(id: number) {
    if (open === id) {
      setOpen(null);
      setMatches(null);
      return;
    }
    setOpen(id);
    setMatches(null);
    try {
      setMatches(await api.watchMatches(id));
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Could not load that alert.");
    }
  }

  async function markRead(id: number) {
    setBusy(true);
    try {
      await api.ackWatch(id);
      await load();
      if (open === id) setMatches(await api.watchMatches(id));
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Could not update that alert.");
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: number) {
    setBusy(true);
    try {
      await api.deleteWatch(id);
      if (open === id) {
        setOpen(null);
        setMatches(null);
      }
      await load();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Could not delete that alert.");
    } finally {
      setBusy(false);
    }
  }

  if (!user?.capabilities.can_watch) {
    return (
      <section className="alerts">
        <h1>Saved alerts</h1>
        <p className="sub">
          Saved alerts start on the County plan. An alert watches a search you
          have already built and tells you what has appeared since you last
          looked.
        </p>
      </section>
    );
  }

  return (
    <section className="alerts">
      <h1>Saved alerts</h1>
      <p className="sub">
        Each alert reports what has entered Distress Signal since you last read
        it. That is our record of when a building appeared here — not a filing
        date, which the counties do not publish for every record.
      </p>

      {error && <div className="notice error">{error}</div>}

      {summaries === null && <p className="sub">Loading…</p>}

      {summaries?.length === 0 && (
        <div className="empty">
          <p>No alerts yet.</p>
          <p className="sub">
            Build a search on the <Link to={DASHBOARD_PATH}>dashboard</Link> —
            a county, a score, milestone overdue — then save it as an alert.
          </p>
        </div>
      )}

      <ul className="alertlist">
        {(summaries ?? []).map(({ watch, new_count, entitled, reason }) => {
          const why = emptyReason(entitled, reason, new_count, watch.acked_at);
          const county = countyName(watch.county_fips);
          return (
            <li key={watch.id} className={entitled ? "" : "muted"}>
              <div className="alerthead">
                <div>
                  <b>{watch.name}</b>
                  <div className="sub">
                    {county ? `${county} County` : "Every county you hold"} ·{" "}
                    {watch.scope === "units" ? "Parcels" : "Buildings"}
                  </div>
                </div>
                <div className="alertcount">
                  <span className={new_count > 0 && entitled ? "badge on" : "badge"}>
                    {entitled ? newCountLabel(new_count, watch.scope) : "Unavailable"}
                  </span>
                </div>
              </div>

              {why && <p className="sub">{why}</p>}
              {!why && entitled && (
                <p className="sub">{measuredFrom(watch.acked_at)}</p>
              )}

              <div className="alertactions">
                {entitled && new_count > 0 && (
                  <button className="linkbtn" onClick={() => void openWatch(watch.id)}>
                    {open === watch.id ? "Hide" : "Show"}
                  </button>
                )}
                {entitled && new_count > 0 && (
                  <button
                    className="linkbtn"
                    disabled={busy}
                    onClick={() => void markRead(watch.id)}
                    title="Moves the mark to now. Cannot be undone."
                  >
                    Mark as read
                  </button>
                )}
                <button
                  className="linkbtn danger"
                  disabled={busy}
                  onClick={() => void remove(watch.id)}
                >
                  Delete
                </button>
              </div>

              {open === watch.id && (
                <div className="alertrows">
                  {matches === null && <p className="sub">Loading…</p>}
                  {matches?.capped && (
                    <p className="sub">
                      Showing {matches.buildings.length + matches.units.length} of{" "}
                      {matches.total.toLocaleString()}. Narrow the alert's filters
                      to see fewer, more relevant rows.
                    </p>
                  )}
                  {matches && matches.buildings.length > 0 && (
                    <BuildingsTable
                      rows={matches.buildings}
                      onSelect={() => undefined}
                      canSeeFolio={user.capabilities.can_see_folio}
                    />
                  )}
                  {matches && matches.units.length > 0 && (
                    <UnitsTable rows={matches.units} />
                  )}
                </div>
              )}

              <div className="sub tiny">
                Created {formatWhen(watch.created_at) || "recently"}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

